/**
 * /workstreams URLs (the page was /workstreams4 until the Oct 26, 2026 swap).
 * The path is the place (My workstreams at the base, the rail's other places,
 * or a workstream); the query narrows it (person, pillar) or sets how it's
 * shown (view, timeframe).
 *
 * The rail only ever focuses a workstream's card (/workstreams/:id); its full
 * page is one level deeper (/workstreams/:id/detail), reached from the card's
 * "More details" button.
 */
export const WORKSTREAMS_BASE = '/workstreams';

export type WorkstreamPage = 'focus' | 'detail';

// The rail's places with a path of their own. They share the route's :id slot
// (ids are numeric), so moving between them keeps the page mounted.
export const WORKSTREAMS_PLACES = ['company', 'audit', 'inbox'] as const;
export type WorkstreamsPlace = (typeof WORKSTREAMS_PLACES)[number];

export const parseWorkstreamsPlace = (segment?: string): WorkstreamsPlace | undefined =>
  WORKSTREAMS_PLACES.find((place) => place === segment);

export const workstreamsPlacePath = (place: WorkstreamsPlace) => `${WORKSTREAMS_BASE}/${place}`;

/** A pillar's workstreams: the Company place narrowed to it. */
export const workstreamsPillarPath = (pillar: string) =>
  `${workstreamsPlacePath('company')}?${new URLSearchParams({pillar})}`;

export const workstreamsPath = (id?: number, page: WorkstreamPage = 'focus') =>
  id === undefined
    ? WORKSTREAMS_BASE
    : `${WORKSTREAMS_BASE}/${id}${page === 'detail' ? '/detail' : ''}`;

/** Which of the page's levels a /workstreams path is: the list, a focused card, or a full page. */
export const workstreamsPageOf = (pathname: string): 'list' | WorkstreamPage => {
  // Off the base, not a literal, so it follows the page if it moves.
  const match = new RegExp(`^${WORKSTREAMS_BASE}/\\d+(/detail)?/?$`).exec(pathname);
  return !match ? 'list' : match[1] ? 'detail' : 'focus';
};

/**
 * Workstream id behind a link the host intercepts: the cards' own
 * /workstreams/:id/detail, or a shared component's /workstreams/:id. Both open
 * the full page. The page's own links to a card (also /workstreams/:id) carry
 * `data-own-link`, and the host leaves them to the router.
 */
export const parseDetailLink = (href: string): number | undefined => {
  const match = new RegExp(`^${WORKSTREAMS_BASE}/(\\d+)(?:[/?#]|$)`).exec(href);
  return match ? Number(match[1]) : undefined;
};

/** A person's workstreams (the bylines link here; the page keeps its other params). */
export const workstreamsPersonPath = (ldap: string) =>
  `${WORKSTREAMS_BASE}?${new URLSearchParams({person: ldap})}`;

/** The ldap behind a link the host intercepts to scope the page to a person. */
export const parsePersonLink = (href: string): string | undefined => {
  const url = new URL(href, 'http://host');
  return url.pathname === WORKSTREAMS_BASE
    ? (url.searchParams.get('person') ?? undefined)
    : undefined;
};
